import { Button, CardProps, Skeleton, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { prettyCurrency } from '@pelicargo/utils';
import { HiChevronRight } from 'react-icons/hi2';
import { Link as RRLink, useNavigate } from 'react-router-dom';

import { TrpcOutputs } from '../../config/trpc';
import { goToQuote } from '../../hooks/quotes/useQuoteHelper';
import { QuoteVariantWithLineItemsPossibleInland, useQuoteVariantHelper } from '../../hooks/useQuoteVariantHelper';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { getBestQuoteVariants } from '../../utils/getTotalCost';
import { Validity } from '../Validity';
import { QuoteVariantUI } from './QuoteVariant';
import { BookingWarnings } from '../BookingWarnings';

type Props = CardProps & {
  quote: TrpcOutputs['getQuote'];
  quoteVariants: TrpcOutputs['getQuote']['quote_variants'][0][];

  navigateOnSelection?: boolean;

  showBookingLink?: boolean;
};

const QuoteVariantTabPlaceholder = ({ label }: { label: string }) => (
  <Tab
    key="skeleton"
    w="full"
    color="text.default"
    borderColor="border.light"
    borderRightWidth={1}
    _last={{ borderRightWidth: '0' }}
    isDisabled
  >
    <Stack direction="row" spacing="1" align="center">
      <Text textStyle="bodyMedium" className="qv-name">
        {label}
      </Text>
    </Stack>
  </Tab>
);

export const QuoteVariantTabs = ({ quote, quoteVariants, navigateOnSelection, showBookingLink = true }: Props) => {
  const navigate = useNavigate();
  const { selectedQuoteVariantId, setSelectedQuoteVariantId } = useSelectedQuoteVariant();
  const bestQuoteVariants = getBestQuoteVariants(quoteVariants || [], selectedQuoteVariantId);
  const quoteVariantId = selectedQuoteVariantId || bestQuoteVariants?.[0]?.id;
  const quoteVariant = bestQuoteVariants?.find((variant) => variant.id === quoteVariantId);
  const tabIndexFromVariant = bestQuoteVariants?.findIndex((variant) => variant.id === quoteVariantId) || 0;

  const { allInVariableRate } = useQuoteVariantHelper(quoteVariant, quote);

  const renderTabSelect = (quoteVariant: QuoteVariantWithLineItemsPossibleInland) => {
    return (
      <QuoteVariantTabHeader
        key={quoteVariant?.id}
        quoteVariant={quoteVariant}
        quote={quote}
        comparePrice={allInVariableRate}
      />
    );
  };

  const renderTab = (quoteVariant: QuoteVariantWithLineItemsPossibleInland) => {
    return (
      <TabPanel key={quoteVariant?.id}>
        <QuoteVariantUI quoteVariant={quoteVariant} quote={quote} />
        <Show if={showBookingLink}>
          <Stack alignItems="flex-end" w="full" pt={2}>
            <Button
              as={RRLink}
              to={goToQuote(quote, quoteVariant)}
              colorScheme="primary"
              size="sm"
              iconSpacing="2"
              minW="113px"
              rightIcon={<HiChevronRight strokeWidth="2" />}
            >
              Book Quote
            </Button>
          </Stack>
        </Show>
        <Validity quote={quote} />
        <BookingWarnings quoteVariantId={quoteVariant?.id} />
      </TabPanel>
    );
  };

  const handleTabSelection = (index: number) => {
    const selectedQuoteVariant = bestQuoteVariants?.[index];
    if (selectedQuoteVariant) setSelectedQuoteVariantId(selectedQuoteVariant?.id);
    if (navigateOnSelection) {
      navigate(goToQuote(quote, selectedQuoteVariant), {
        replace: true,
      });
    }
  };

  return (
    <Tabs index={bestQuoteVariants?.length ? tabIndexFromVariant : 0} onChange={handleTabSelection}>
      <TabList overflowX="auto" overflowY="hidden">
        {bestQuoteVariants?.map(renderTabSelect)}
        <Show if={!bestQuoteVariants?.length}>
          <QuoteVariantTabPlaceholder label="General" />
          <QuoteVariantTabPlaceholder label="Expedited" />
        </Show>
      </TabList>
      <TabPanels>
        {bestQuoteVariants?.map(renderTab)}
        <Show if={!bestQuoteVariants?.length}>
          <TabPanel key="skeleton">
            <Stack>
              <Skeleton height="10px" width="50%" />
              <Skeleton height="10px" width="30%" />
              <Skeleton height="10px" width="70%" />
            </Stack>
          </TabPanel>
        </Show>
      </TabPanels>
    </Tabs>
  );
};

const QuoteVariantTabHeader = ({
  quote,
  quoteVariant,
  comparePrice,
}: {
  quote: TrpcOutputs['getQuote'];
  quoteVariant: QuoteVariantWithLineItemsPossibleInland;
  comparePrice: number;
}) => {
  const { allInVariableRate } = useQuoteVariantHelper(quoteVariant, quote);
  const differenceInCents = allInVariableRate - comparePrice;
  const prettyDifference = prettyCurrency(differenceInCents);

  return (
    <Tab
      key={quoteVariant?.id}
      w="full"
      color="text.default"
      borderColor="border.light"
      borderRightWidth={1}
      _last={{ borderRightWidth: '0' }}
      _selected={{
        borderBottomColor: 'primary.600',
        color: 'primary.600',
        '& .qv-name': { color: 'primary.600' },
        '& .qv-difference': { display: 'none' },
      }}
    >
      <Stack direction="row" spacing="1" align="center">
        <Text textStyle="bodyMedium" className="qv-name">
          {quoteVariant?.type}
        </Text>
        <Text textStyle="bodySmallRegular" color="text.subtle" className="qv-difference">
          {differenceInCents < 0 ? `${prettyDifference}/kg` : `+${prettyDifference}/kg`}
        </Text>
      </Stack>
    </Tab>
  );
};
